<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <hero-bar>
      Clearing logging
      <router-link slot="right" to="/" class="button">
        Dashboard
      </router-link>
    </hero-bar>
    <section class="section is-main-section">
      <card-component>
        <em>
          <p class="pb-2">View the log of messages that are either received or sent out by the connector.</p>
        </em>
      </card-component>
      <card-component
        title="Logs"
        class="has-table has-mobile-sort-spaced"
        icon="routes"
        headerColor="info"
        header-icon="magnify"
        @header-icon-click="search = !search"
      >
        <template #addition>
          <b-select class="mt-3" v-model="interval" placeholder="Refresh interval">
            <option value="1000">1 second</option>
            <option value="5000">5 seconds</option>
            <option value="10000">10 seconds</option>
            <option value="30000">30 seconds</option>
            <option value="60000">60 seconds</option>
          </b-select>
        </template>
        <template #default>
          <b-table
            :loading="isLoading"
            :paginated="paginated"
            :per-page="15"
            :striped="true"
            :hoverable="true"
            default-sort="name"
            :data="clearing"
          >
            <b-table-column label="Time" field="timestamp" sortable v-slot="props">
              {{ formatTimestamp(props.row.timestamp) }}
            </b-table-column>
            <b-table-column label="Direction" field="direction" sortable v-slot="props" :searchable="search">
              {{ props.row.direction[0] + props.row.direction.substring(1).toLowerCase() }}
            </b-table-column>
            <b-table-column label="Context" field="context" sortable v-slot="props" :searchable="search">
              {{ props.row.context[0] + props.row.context.substring(1).toLowerCase() }}
            </b-table-column>
            <b-table-column label="Remote connector" field="remoteConnector" sortable v-slot="props" :searchable="search">
              {{ props.row.remoteConnector }}
            </b-table-column>
            <b-table-column label="Type" field="type" sortable v-slot="props" :searchable="search">
              {{ props.row.type }}
            </b-table-column>
            <b-table-column label="Details" field="detail" v-slot="props">
              <b-icon
                icon="file-search"
                type="is-info"
                class="is-clickable"
                @click.native="detailModal = {isActive: true, message: props.row}"
              >
              </b-icon>
            </b-table-column>
            <section slot="empty" class="section">
              <div class="content has-text-grey has-text-centered">
                <p>No logged messages present...</p>
              </div>
            </section>
          </b-table>
        </template>
      </card-component>
    </section>

    <b-modal :active.sync="detailModal.isActive" has-modal-card>
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">Resource Offer</p>
        </header>
        <section class="modal-card-body" v-if="detailModal.message">
          <b-field label="Time" horizontal>
            <p class="control">
              {{ formatTimestamp(detailModal.message.timestamp) }}
            </p>
          </b-field>
          <b-field label="Type" horizontal>
            <p class="control">
              {{ detailModal.message.type }}
            </p>
          </b-field>
          <b-field label="Remote Connector" horizontal>
            <p class="control">
              {{ detailModal.message.remoteConnector }}
            </p>
          </b-field>
          <b-field label="Direction" horizontal>
            <p class="control">
              {{ detailModal.message.direction[0] + detailModal.message.direction.substring(1).toLowerCase() }}
            </p>
          </b-field>
          <b-field label="Context" horizontal>
            <p class="control">
              {{ detailModal.message.context[0] + detailModal.message.context.substring(1).toLowerCase() }}
            </p>
          </b-field>
          <b-field label="Properties" v-if="detailModal.message.properties" horizontal>
            <p class="control">
              <b-field grouped group-multiline>
                <div class="control">
                  <b-taglist v-for="(value, key) in detailModal.message.properties" :key="`detail-property-${key}`" attached>
                      <b-tag type="is-dark">{{ key }}</b-tag>
                      <b-tag type="is-info">{{ value }}</b-tag>
                  </b-taglist>
                </div>
              </b-field>
            </p>
          </b-field>
          <b-field label="Header">
            <vue-json-editor v-model="detailModal.message.header" :modes="['view', 'text']" mode="view" :show-btns="false" :expandedOnStart="true"></vue-json-editor>
          </b-field>
          <b-field label="Body" v-if="detailModal.message.body">
            <vue-json-editor v-if="detailModal.message.jsonBody" v-model="detailModal.message.jsonBody" :modes="['view', 'text']" mode="view" :show-btns="false" :expandedOnStart="true"></vue-json-editor>
            <pre v-else><code>{{ detailModal.message.body }}</code></pre>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <button class="button" type="button" @click="detailModal.isActive = false">Close</button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TitleBar from '@/components/TitleBar'
import CardComponent from '@/components/CardComponent'
import HeroBar from '@/components/HeroBar'
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import vueJsonEditor from 'vue-json-editor'

export default {
  name: 'Clearing',
  components: {
    HeroBar,
    CardComponent,
    TitleBar,
    vueJsonEditor
  },
  data () {
    return {
      isLoading: false,
      paginated: true,
      search: false,
      interval: 5000,
      clearing: [],
      detailModal: {
        isActive: false,
        message: null
      }
    }
  },
  computed: {
    titleStack () {
      return ['Clearing', 'Logs']
    },
    ...mapState(['api'])
  },
  async created () {
    this.routeInterval = setInterval(this.getClearing, this.interval)
    await this.getClearing()
  },
  beforeDestroy () {
    clearInterval(this.routeInterval)
  },
  watch: {
    interval (newVal) {
      clearInterval(this.routeInterval)
      this.getClearing()
      this.routeInterval = setInterval(this.getClearing, newVal)
    }
  },
  methods: {
    async getClearing () {
      try {
        var from = ''
        if (this.clearing.length > 0) {
          from = `?from=${Math.max(...this.clearing.map(message => message.timestamp)) + 1}`
        }
        const clearing = await this.api.get(`/clearing${from}`)
        this.clearing = clearing.data.map(message => {
          if (message.direction === 'INGRESS') {
            message.remoteConnector = message.header['ids:issuerConnector']
          } else {
            message.remoteConnector = message.header['ids:recipientConnector'][0]
          }
          message.type = message.header['@type'].substring(4).replace(/([A-Z])/g, ' $1').trim()
          if (message.body) {
            try {
              message.jsonBody = JSON.parse(message.body)
            } catch (_) {}
          }
          return message
        }).concat(this.clearing)
      } catch (e) {
        clearInterval(this.routeInterval)
        this.$buefy.snackbar.open({
          message: `Error: ${e.message}`,
          type: 'is-danger',
          position: 'is-bottom',
          duration: 10000,
          queue: false,
          cancelText: 'OK',
          actionText: 'Retry',
          onAction: () => {
            clearInterval(this.routeInterval)
            this.getClearing()
            this.routeInterval = setInterval(this.getClearing, this.interval)
          }
        })
        console.log(e)
      }
    },
    formatUptime (uptimeMillis) {
      if (uptimeMillis) {
        return moment.duration(uptimeMillis, 'milliseconds').humanize()
      } else {
        return null
      }
    },
    formatTimestamp (timestamp) {
      if (timestamp) {
        return moment(timestamp).format('DD-MM-YYYY HH:mm:ss z')
      } else {
        return null
      }
    }
  }
}
</script>
